import { useNotify } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { ChurnSurveyFormLoader, ChurnSurveyForm } from './ChurnSurveyForm';
import { useChurnSurvey } from '../hooks/useChurnSurvey';
import type { FormResult } from '../types';

interface Props {
  formId: string;
  onSubmit(): void;
  accountId: string;
  subscriptionSubtotal: string;
}

/**
 * This component renders the Churn Survey that is shown for customer on billing 3.
 * It is known as 'Churn/Cancellation Survey 2.0'
 *
 * https://hotjar.atlassian.net/wiki/spaces/PR/pages/3258026853/Cancellation+2.0+survey
 */
export function ChurnSurvey({
  onSubmit,
  formId,
  accountId,
  subscriptionSubtotal,
}: Props): JSX.Element {
  const { questions, submitSurvey, formReady } = useChurnSurvey({
    accountId,
    subscriptionSubtotal,
  });
  const { showToast } = useNotify();
  const { t } = useTranslationWithPrefix('checkout:churn.surveyv2');

  async function handleSubmit(formData: FormResult) {
    try {
      await submitSurvey(formData);
      onSubmit();
    } catch {
      showToast({ text: t('error-submitting'), kind: 'danger' });
    }
  }

  if (!formReady) {
    return <ChurnSurveyFormLoader />;
  }

  return (
    <ChurnSurveyForm
      formId={formId}
      downgradeReason={questions?.downgradeReason}
      downgradeExplanations={questions?.downgradeExplanations}
      onSubmit={handleSubmit}
    />
  );
}
